import React from 'react';
import { useState } from 'react';
import { 
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ChartPieIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { MetricCard } from './MetricCard';
import { useProjectStore } from '../store/projectStore';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';

type TimeRange = '1d' | '1w' | '1m' | '1y' | 'ytd' | 'all';

interface ProjectCardProps {
  project: any;
  id: number;
}

function ProjectCard({ project, id }: ProjectCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getProjectProgress = (project: any) => {
    switch (project.status) {
      case 'Completed': return 100;
      case 'In Progress': return 65;
      case 'Planning': return 25;
      default: return 0;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="bg-gray-900/80 rounded-xl p-6 cursor-move hover:bg-gray-800/80 transition-colors flex flex-col border border-gray-700/30 backdrop-blur-sm shadow-lg"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="text-4xl bg-gray-800/50 p-3 rounded-lg">
          {project.client === 'TechCorp Industries' ? '🏢' : 
           project.client === 'SecureBank Global' ? '🏦' : '🏥'}
        </div>
        <span className={clsx(
          'px-3 py-1 text-xs font-medium rounded-full',
          project.status === 'Completed' ? 'bg-green-500/20 text-green-400 border border-green-500/20' :
          project.status === 'In Progress' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/20' :
          'bg-yellow-500/20 text-yellow-400 border border-yellow-500/20'
        )}>
          {project.status}
        </span>
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-medium text-white mb-1">{project.name}</h3>
        <div className="flex items-center text-sm text-gray-400">
          <BuildingOfficeIcon className="h-4 w-4 mr-1" />
          {project.client}
        </div>
      </div>

      <div className="space-y-4 flex-1">
        <div>
          <div className="flex justify-between text-sm text-gray-400 mb-1">
            <span>Progress</span>
            <span>{getProjectProgress(project)}%</span>
          </div>
          <div className="w-full bg-gray-950/50 rounded-full h-2.5">
            <div
              className={clsx(
                "h-2.5 rounded-full transition-all duration-500",
                project.status === 'Completed' ? "bg-green-500" :
                project.status === 'In Progress' ? "bg-blue-400" :
                "bg-yellow-400"
              )}
              style={{ width: `${getProjectProgress(project)}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center bg-gray-800/50 rounded-lg px-3 py-2">
            <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
            <div>
              <div className="text-white font-medium">
                {project.resourceCount}
              </div>
              <div className="text-xs text-gray-400">Team</div>
            </div>
          </div>
          <div className="flex items-center bg-gray-800/50 rounded-lg px-3 py-2">
            <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-2" />
            <div>
              <div className="text-white font-medium">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  notation: 'compact',
                  maximumFractionDigits: 1
                }).format(45000)}
              </div>
              <div className="text-xs text-gray-400">Rev</div>
            </div>
          </div>
          <div className="flex items-center bg-gray-800/50 rounded-lg px-3 py-2">
            <ChartPieIcon className="h-4 w-4 text-gray-400 mr-2" />
            <div>
              <div className="text-white font-medium">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  notation: 'compact',
                  maximumFractionDigits: 1
                }).format(12000)}
              </div>
              <div className="text-xs text-gray-400">Profit</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DashboardProps {
  username: string;
  role: 'Executive' | 'Project Manager';
}

const metrics = [
  {
    title: 'Monthly Revenue',
    value: '$45,231',
    change: { value: 12.5, isPositive: true },
    icon: CurrencyDollarIcon
  },
  {
    title: 'Project Profitability',
    value: '84.3%',
    change: { value: 3.2, isPositive: true },
    icon: ChartPieIcon
  },
  {
    title: 'Resource Utilization',
    value: '92%',
    change: { value: 2.1, isPositive: false },
    icon: UserGroupIcon
  },
  {
    title: 'Projected Revenue',
    value: '$67,492',
    change: { value: 18.3, isPositive: true },
    icon: ArrowTrendingUpIcon
  }
];

const pmMetrics = [
  {
    title: 'Active Projects',
    value: '3',
    change: { value: 1, isPositive: true },
    icon: ChartPieIcon
  },
  {
    title: 'Team Utilization',
    value: '92%',
    change: { value: 2.1, isPositive: false },
    icon: UserGroupIcon
  },
  {
    title: 'Upcoming Milestones',
    value: '5',
    change: { value: 2, isPositive: true },
    icon: ArrowTrendingUpIcon
  }
];
export function Dashboard({ username, role }: DashboardProps) {
  const { projects } = useProjectStore();
  const [timeRange, setTimeRange] = useState<TimeRange>('1m');
  const [items, setItems] = useState(projects.map(p => p.id));
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: any) {
    const {active, over} = event;
    
    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const getProjectProgress = (project: any) => {
    switch (project.status) {
      case 'Completed': return 100;
      case 'In Progress': return 65;
      case 'Planning': return 25;
      default: return 0;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Welcome, {username}
            </h1>
            <p className="text-gray-400">
              {role === 'Executive' 
                ? "Here's your executive overview"
                : "Here's your project management overview"
              }
            </p>
          </div>
          <div className="flex gap-2">
            {(['1d', '1w', '1m', '1y', 'ytd', 'all'] as TimeRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  timeRange === range
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                )}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            change={metric.change}
            icon={metric.icon}
            timeRange={timeRange}
          />
        ))}
      </div>
      
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-red-500 mr-2" />
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            Project Overview
            <span className="ml-2 text-sm text-gray-400">({timeRange.toUpperCase()})</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={items}>
              {items.map((id) => (
                <ProjectCard
                  key={id}
                  id={id}
                  project={projects.find(p => p.id === id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
}